/**
 * useThumbnailCapture — generates a dashboard thumbnail from the real rendered DOM.
 *
 * Uses a hidden offscreen ECharts canvas instance to render the thumbnail
 * without affecting the visible charts. The canvas renderer supports
 * getDataURL() which exports directly to a base64 PNG.
 */
import { useRef, useCallback, useEffect } from 'react';
import * as echarts from 'echarts/core';
import { SVGRenderer } from 'echarts/renderers';
import { dashboardsService } from '../api/dashboardsService';
import { api } from '../api/client';

// Register SVGRenderer for offscreen thumbnail rendering
echarts.use([SVGRenderer]);

const DEBOUNCE_MS = 1000;
const THUMB_WIDTH = 400;
const THUMB_HEIGHT = 225;

/** Chart types eligible for thumbnail capture (visual charts, not KPI/table) */
const ELIGIBLE_TYPES = new Set([
  'line', 'area', 'bar', 'histogram', 'pie', 'funnel', 'scatter', 'heatmap',
]);

interface ChartInfo {
  id: string;
  chartType: string;
  orderIndex: number;
}

/**
 * Select the first eligible (non-KPI, non-table) chart by orderIndex.
 * Returns null if no eligible chart exists.
 */
function selectPrimaryChart(charts: ChartInfo[]): ChartInfo | null {
  const eligible = charts
    .filter(c => ELIGIBLE_TYPES.has(c.chartType))
    .sort((a, b) => a.orderIndex - b.orderIndex);
  return eligible[0] ?? null;
}

/**
 * Capture a specific chart element and upload as dashboard thumbnail.
 * Creates a hidden canvas-based ECharts instance, renders the chart there,
 * exports via getDataURL(), then disposes. No visible chart is affected.
 */
export async function captureAndUploadThumbnail(chartId: string, dashboardId: string): Promise<void> {
  const el = document.querySelector(`[data-chart-id="${chartId}"]`) as HTMLElement | null;
  if (!el) throw new Error('Chart element not found');

  // Find the live ECharts instance to copy its option
  const chartDiv = el.querySelector('div[_echarts_instance_]') as HTMLElement | null
    ?? el.querySelector('div > div') as HTMLElement | null;
  const liveInstance = chartDiv ? echarts.getInstanceByDom(chartDiv) : null;
  if (!liveInstance) throw new Error('ECharts instance not found');

  const currentOption = liveInstance.getOption();

  // Thumbnail overrides (strip title/legend/axis names, minimize margins)
  const thumbOption = {
    ...currentOption,
    title: [{ show: false }],
    legend: [{ show: false }],
    grid: Array.isArray(currentOption.grid)
      ? (currentOption.grid as any[]).map((g: any) => ({ ...g, left: 4, right: 4, top: 4, bottom: 4, containLabel: false }))
      : [{ left: 4, right: 4, top: 4, bottom: 4, containLabel: false }],
    ...(currentOption.xAxis ? {
      xAxis: Array.isArray(currentOption.xAxis)
        ? (currentOption.xAxis as any[]).map((ax: any) => ({ ...ax, name: '', show: false }))
        : [{ ...(currentOption.xAxis as any)?.[0] || currentOption.xAxis, name: '', show: false }],
    } : {}),
    ...(currentOption.yAxis ? {
      yAxis: Array.isArray(currentOption.yAxis)
        ? (currentOption.yAxis as any[]).map((ax: any) => ({ ...ax, name: '', show: false }))
        : [{ ...(currentOption.yAxis as any)?.[0] || currentOption.yAxis, name: '', show: false }],
    } : {}),
    tooltip: [{ show: false }],
    animation: false,
  };

  // Render both light and dark variants
  // Background colors match Fluent tokens.colorNeutralBackground1
  const lightDataUrl = renderThumbnail(thumbOption, '#ffffff', false);
  const darkDataUrl = renderThumbnail(thumbOption, '#292929', true);

  await dashboardsService.uploadThumbnail(dashboardId, lightDataUrl, darkDataUrl);

  // Store version so list page can bust cache immediately
  sessionStorage.setItem(`thumb_v_${dashboardId}`, String(Date.now()));
}

/** Render a thumbnail offscreen as SVG and return a data:image/svg+xml data URL */
function renderThumbnail(option: Record<string, any>, backgroundColor: string, isDark: boolean): string {
  const offscreen = document.createElement('div');
  offscreen.style.cssText = 'position:absolute;left:-9999px;top:-9999px;width:400px;height:225px;visibility:hidden;pointer-events:none;';
  document.body.appendChild(offscreen);

  let offInstance: ReturnType<typeof echarts.init> | null = null;

  try {
    offInstance = echarts.init(offscreen, isDark ? 'dark' : undefined, {
      renderer: 'svg',
      width: THUMB_WIDTH,
      height: THUMB_HEIGHT,
    });

    // Override ECharts dark theme default background
    if (isDark) {
      offInstance.setOption({ backgroundColor: 'transparent' });
    }

    offInstance.setOption(option);

    // Extract the rendered SVG string from the DOM
    const svgElement = offscreen.querySelector('svg');
    if (!svgElement) throw new Error('SVG element not found after render');

    // Add background rect and set viewBox
    svgElement.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    svgElement.setAttribute('width', String(THUMB_WIDTH));
    svgElement.setAttribute('height', String(THUMB_HEIGHT));

    // Insert background rect as first child
    const bgRect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
    bgRect.setAttribute('width', '100%');
    bgRect.setAttribute('height', '100%');
    bgRect.setAttribute('fill', backgroundColor);
    svgElement.insertBefore(bgRect, svgElement.firstChild);

    const svgString = new XMLSerializer().serializeToString(svgElement);
    const encoded = new TextEncoder().encode(svgString);
    const binary = Array.from(encoded, (b) => String.fromCharCode(b)).join('');
    const dataUrl = 'data:image/svg+xml;base64,' + btoa(binary);

    return dataUrl;
  } finally {
    if (offInstance) offInstance.dispose();
    document.body.removeChild(offscreen);
  }
}

/** Get cache-bust version for a dashboard thumbnail URL */
export function getThumbnailVersion(dashboardId: string): string {
  return sessionStorage.getItem(`thumb_v_${dashboardId}`) || String(Math.floor(Date.now() / 60000));
}

/** Check if a thumbnail already exists on the server */
async function thumbnailExists(dashboardId: string): Promise<boolean> {
  try {
    await api.head(`/dashboards/${dashboardId}/thumbnail`, { suppressErrorCapture: true });
    return true;
  } catch {
    return false;
  }
}

export function useThumbnailCapture(
  dashboardId: string | undefined,
  charts: ChartInfo[],
) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const capturedRef = useRef(false);

  // Reset captured flag when dashboard changes
  useEffect(() => {
    capturedRef.current = false;
  }, [dashboardId]);

  const capture = useCallback(async () => {
    if (!dashboardId || !charts.length) return;
    if (capturedRef.current) return;

    // Check if thumbnail already exists on server
    const exists = await thumbnailExists(dashboardId);
    if (exists) {
      capturedRef.current = true;
      return;
    }

    const primary = selectPrimaryChart(charts);
    if (!primary) return;

    capturedRef.current = true;

    try {
      await captureAndUploadThumbnail(primary.id, dashboardId);
    } catch (e) {
      console.warn('[Thumbnail] Capture/upload failed:', e);
      capturedRef.current = false; // Allow retry
    }
  }, [dashboardId, charts]);

  // Listen for 'echart-rendered' — fired by useECharts after first setOption + paint
  useEffect(() => {
    if (!dashboardId || !charts.length) return;

    const primary = selectPrimaryChart(charts);
    if (!primary) return;

    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.chartId === primary.id) {
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => {
          timerRef.current = null;
          capture();
        }, DEBOUNCE_MS);
      }
    };

    window.addEventListener('echart-rendered', handler);

    // Fallback: retry periodically in case the event was missed.
    let fallbackAttempts = 0;
    const maxAttempts = 5;
    const fallbackInterval = setInterval(() => {
      fallbackAttempts++;
      if (capturedRef.current || fallbackAttempts >= maxAttempts) {
        clearInterval(fallbackInterval);
        return;
      }
      capture();
    }, 5000);

    return () => {
      window.removeEventListener('echart-rendered', handler);
      clearInterval(fallbackInterval);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [dashboardId, charts, capture]);
}